import Camera from "_src/engine/camera";
import renderer from "_src/engine/renderer";
import extensions from "_src/engine/extensions";

import MainPass from "_src/render_graph/passes/main_pass";
import InterfacePass from "_src/render_graph/passes/interface_pass";
import GridPass from "_src/render_graph/passes/grid_pass";
import MirrorPass from "_src/render_graph/passes/mirror_pass";
import ReadColorPass from "_src/render_graph/passes/read_color_pass";
import WireframePass from "_src/render_graph/passes/wireframe_pass";
import TransformPass from "_src/render_graph/passes/transform_pass";
import ViewCubePass from "_src/render_graph/passes/viewcube_pass";
import HighlightPass from "_src/render_graph/passes/highlight_pass";
import SelectionPass from "_src/render_graph/passes/selection_pass";
import AntialiasingPass from "_src/render_graph/passes/antialiasing_pass";
import LassoPass from "_src/render_graph/passes/lasso_pass";
import PostEffectPass from "_src/render_graph/passes/post_effect_pass";
import CopyPass from "_src/render_graph/passes/copy_pass";

import {attachmentId} from "_src/engine/constants";
import memoryProfiler from "_src/engine/memory_profiler";
import config from "_src/config";
import BackgroundPass from "_src/render_graph/passes/background_pass";
import MeasureLinesPass from "_src/render_graph/passes/measure_lines_pass";
import CompareDrawPass from "_src/render_graph/passes/compare_draw_pass";
import CompareMixPass from "_src/render_graph/passes/compare_mix_pass";
import PhotosPass from "_src/render_graph/passes/photos_pass";
import monitoring from "_src/monitoring";

const NULL_LINK = -1;

const BACKGROUND    = 0;
const MAIN          = 1;
const COMPARE_DRAW  = 2;
const GRID          = 3;
const WIREFRAME     = 4;
const OUTLINE       = 5;
const INTERFACE     = 6;
const PHOTOS        = 7;
const MEASURE_LINES = 8;
const MIRROR        = 9;
const COPY          = 10;
const COMPARE_MIX   = 11;
const LASSO         = 12;
const ANTIALIASING  = 13;
const SELECTION     = 14;
const READ_COLOR    = 15;
const TRANSFORM     = 16;
const POSTEFFECT    = 17;
const VIEWCUBE      = 18;

export class RenderGraph {

  constructor(isMain, cameraType) {

    this._isMain = isMain;
    this._nodes = [];
    this._attachmentsStorage = [];
    this._frameRequested = true;
    this._fullUpdate = false;

    const workspaceCamera = new Camera(cameraType);
    const gridCamera = isMain && config.recalculateInterfaceDepth ? new Camera() : workspaceCamera;
    const screenQuadCamera = new Camera();
    const workspaceCopyCamera = new Camera();
    const previewCamera = new Camera();

    const backgroundPass = new BackgroundPass(this._attachmentsStorage);
    const mainPass = new MainPass(this._attachmentsStorage, workspaceCamera);
    const compareDrawPass = new CompareDrawPass(this._attachmentsStorage, workspaceCamera);
    const gridPass = new GridPass(this._attachmentsStorage, gridCamera, workspaceCopyCamera);
    const wireframePass = new WireframePass(this._attachmentsStorage, screenQuadCamera);
    const outlinePass = new HighlightPass(this._attachmentsStorage, screenQuadCamera);
    const interfacePass = new InterfacePass(this._attachmentsStorage, gridCamera);
    const photosPass = new PhotosPass(this._attachmentsStorage, workspaceCamera);
    const measureLinesPass = new MeasureLinesPass(this._attachmentsStorage, gridCamera);
    const mirrorPass = new MirrorPass(this._attachmentsStorage, gridCamera);
    const copyPass = new CopyPass(this._attachmentsStorage);
    const compareMixPass = new CompareMixPass(this._attachmentsStorage);
    const selectionPass = new SelectionPass(this._attachmentsStorage, screenQuadCamera);
    const lassoPass = new LassoPass(this._attachmentsStorage, screenQuadCamera);
    const AAPass = new AntialiasingPass(this._attachmentsStorage, screenQuadCamera);
    const readColorPass = new ReadColorPass(this._attachmentsStorage, previewCamera);
    const transformPass = new TransformPass(this._attachmentsStorage, gridCamera, screenQuadCamera);
    const postEffectPass = new PostEffectPass(this._attachmentsStorage);
    const viewcubePass = new ViewCubePass(this._attachmentsStorage);

    this._nodes.push(backgroundPass, mainPass, compareDrawPass, gridPass, wireframePass, outlinePass, interfacePass, photosPass,
      measureLinesPass, mirrorPass, copyPass, compareMixPass, lassoPass, AAPass, selectionPass, readColorPass, transformPass,
      postEffectPass, viewcubePass);
    this._registerScenes();

    this._start();

    this.getWorkspaceScene().updateLights(config.lights);
    this.getSurfelsAdditiveScene().updateLights(config.lights);
    this.getInterfaceScene().updateLights(config.widgetsLights);
  }

  requestFrame(fullUpdate = true) {
    this._frameRequested = true;
    this._fullUpdate = this._fullUpdate || fullUpdate;
  }

  isFrameRequested() {
    return this._frameRequested;
  }

  render(dt = 0) {
    if (this._fullUpdate) monitoring.reportFrameTime(dt);

    const firstPass = this._fullUpdate ? BACKGROUND : COPY;
    for (let i = firstPass; i < this._nodes.length; ++i) {

      const pass = this._nodes[i];

      if (pass.enabled) {
        pass.render(!this._isMain);
      }

    }

    this._frameRequested = false;
    this._fullUpdate = false;
  }

  getPass(index) {
    return this._nodes[index];
  }

  getNodes() {
    return this._nodes;
  }

  setEnabled(passID, enabled) {

    const pass = this.getPass(passID);

    if (pass.enabled === enabled) {
      return;
    }

    const mainPass = this.getPass(MAIN);
    const compareDrawPass = this.getPass(COMPARE_DRAW);

    switch(passID) {
      case WIREFRAME:
        mainPass.enableTriangleIDTexture = enabled;
        this._refreshPass(MAIN);
        compareDrawPass.enableTriangleIDTexture = enabled;
        this._refreshPass(COMPARE_DRAW);
        break;
    }

    if (enabled) {
      this._enablePass(passID);
    } else {
      this._disablePass(passID);
    }

    switch(passID) {
      case COMPARE_DRAW:
        this._refreshPass(WIREFRAME);
        break;
      case GRID:
        this._refreshPass(INTERFACE);
        break;
    }

  }

  checkEnabled(passID) {

    const pass = this.getPass(passID);
    return pass.enabled;

  }

  getInterfaceScene() {
    return this._nodes[INTERFACE].getSecondScene();
  }

  getPostBlendingInterfaceScene() {
    return this._nodes[INTERFACE].getScenes()[2];
  }

  getWorkspaceScene() {
    return this._nodes[MAIN].getMainScene();
  }

  getPhotosScene() {
    return this._nodes[PHOTOS].getMainScene();
  }

  getCadsAdditionalScene() {
    return this._nodes[MAIN].getScenes()[8];
  }

  getSurfelsDepthScene() {
    return this._nodes[MAIN].getScenes()[3];
  }

  getSurfelsAdditiveScene() {
    return this._nodes[MAIN].getScenes()[4];
  }

  getPostBlendingScene() {
    return this._nodes[MAIN].getPostBlendingScene();
  }

  setSurfelsBlendingEnabled(enabled) {
    this._nodes[MAIN].getScenes()[3].setEnabled(enabled);
    this._nodes[MAIN].getScenes()[4].setEnabled(enabled);
    this._nodes[MAIN].getScenes()[5].setEnabled(enabled);
  }

  setPostBlendingEnabled(enabled) {
    this._nodes[MAIN].getScenes()[6].setEnabled(enabled);
    this._nodes[MAIN].getScenes()[7].setEnabled(enabled);
  }

  getTransformScene() {
    return this._nodes[TRANSFORM].getSecondScene();
  }

  getOutputTexture() {
    return this._attachmentsStorage[attachmentId.OUTPUT_COLOR_TEXTURE];
  }

  resize(width, height) {
    for (let attachment of this._attachmentsStorage) {
      if (attachment) {
        attachment.resize(width, height);
      }
    }
    for (let node of this._nodes) {
      node.resize(width, height);
    }
  }

  getObjectIDBuffer(viewport) {
    const stage = 9; // number of the scene
    const pass = this.getPass(MAIN);
    return this._getBuffer(pass, stage, viewport);
  }

  getGeneralPurposeBuffer(viewport) {
    const stage = 10; // number of the scene
    const pass = this.getPass(MAIN);
    return this._getBuffer(pass, stage, viewport);
  }

  getSectionAnalysisBuffer(viewport) {
    const stage = 0; // number of the scene
    const pass = this.getPass(INTERFACE);
    return this._getBuffer(pass, stage, viewport);
  }

  _getBuffer(pass, stage, viewport) {
    this.requestFrame();
    return pass.readPixels(stage, viewport);
  }

  _start() {

    const mainPass = this.getPass(MAIN);

    mainPass.enableColorTexture = true;

    this._enablePass(BACKGROUND);
    this._enablePass(MAIN);
    this._enablePass(INTERFACE);
    this._enablePass(COPY);

    if (config.enableAntialiasing) {
      this._enablePass(ANTIALIASING);
    }

  }

  _registerScenes() {
    for (let i=0; i<this._nodes.length; i++){
      for (const scene of this._nodes[i].scenes) {
        memoryProfiler.registerScene(scene, this, i);
      }
    }
  }

  _findNextPassID(passID) {

    for (let i = passID + 1; i < this._nodes.length; ++i) {

      const node = this._nodes[i];
      if (node.enabled) {
        return i;
      }

    }

    return NULL_LINK;

  }

  _findPrevPassID(passID) {

    for (let i = passID - 1; i >= 0; --i) {

      const node = this._nodes[i];

      if (node.enabled) {
        return i;
      }

    }

    return NULL_LINK;

  }

  _refreshPass(passID) {

    let pass = this.getPass(passID);
    pass.update();

  }

  _enablePass(passID) {

    const pass = this.getPass(passID);
    pass.enable();
    pass.update();

  }

  _disablePass(passID) {

    const pass = this.getPass(passID);
    pass.disable();

  }

  cleanup() {

    for (let i = 0; i < this._nodes.length; ++i) {
      this._nodes[i].disable();
      this._nodes[i].destroy();
    }

    this._nodes.length = 0;
    this._attachmentsStorage.length = 0;

  }

}

export default {
  // these ids have to correspond with passes' positions in the "nodes" storage
  BACKGROUND,
  MAIN,
  COMPARE_DRAW,
  GRID,
  OUTLINE,
  WIREFRAME,
  INTERFACE,
  PHOTOS,
  MEASURE_LINES,
  MIRROR,
  COPY,
  COMPARE_MIX,
  READ_COLOR,
  TRANSFORM,
  SELECTION,
  LASSO,
  VIEWCUBE,
  ANTIALIASING,
  POSTEFFECT,

  cleanup() {
    renderer.cleanup();
    extensions.cleanup();
  },

  getPassName(passID) {

    switch(passID) {
      case this.MAIN:
        return "MAIN";
      case this.COMPARE_DRAW:
        return "COMPARE_DRAW";
      case this.GRID:
        return "GRID";
      case this.OUTLINE:
        return "OUTLINE";
      case this.WIREFRAME:
        return "WIREFRAME";
      case this.INTERFACE:
        return "INTERFACE";
      case this.PHOTOS:
        return "PHOTOS";
      case this.MEASURE_LINES:
        return this.MEASURE_LINES;
      case this.MIRROR:
        return "MIRROR";
      case this.COPY:
        return "COPY";
      case this.COMPARE_MIX:
        return "COMPARE_MIX";
      case this.READ_COLOR:
        return "READ_COLOR";
      case this.TRANSFORM:
        return "TRANSFORM";
      case this.SELECTION:
        return "SELECTION";
      case this.LASSO:
        return "LASSO";
      case this.VIEWCUBE:
        return "VIEWCUBE";
      case this.ANTIALIASING:
        return "ANTIALIASING";
      case this.POSTEFFECT:
        return "POSTEFFECT";
  }

}
}
